
import { supabase } from '@/integrations/supabase/client';
import { logInfo, logError, logDebug } from '@/utils/logging';

/**
 * Session tracking utility that consolidates session management logic 
 * to prevent duplicate tracking and unnecessary reloads
 */

// Flag to track if session start has been recorded
let sessionStarted = false;
// Store the session ID once created
let currentSessionId: string | null = null;

/**
 * Start a user session and record it in the database
 * Only creates one session even if called multiple times
 */
export const startUserSession = async (): Promise<string | null> => {
  if (sessionStarted && currentSessionId) {
    logDebug('Session already started');
    return currentSessionId;
  }

  try {
    const { data, error } = await supabase.functions.invoke('manage-user-sessions', {
      body: { action: 'start_session' }
    });
    
    if (error) {
      logError('Error starting session', error);
      return null;
    }
    
    sessionStarted = true;
    currentSessionId = data;
    logInfo('User session started');
    return data;
  } catch (error) {
    logError('Exception starting session', error);
    return null;
  }
};

/**
 * End a user session when the user logs out or closes the app
 */
export const endUserSession = async (): Promise<void> => {
  if (!sessionStarted || !currentSessionId) {
    logDebug('No active session to end');
    return;
  }
  
  try {
    // Using sendBeacon for more reliable delivery during page unload
    if (navigator.sendBeacon) {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL || 'https://csozwfcytrcqzctrqooj.supabase.co'}/functions/v1/manage-user-sessions`;
      
      navigator.sendBeacon(
        functionUrl,
        JSON.stringify({ 
          action: 'end_session',
          session_id: currentSessionId
        })
      );
      
      logInfo('User session ended with beacon');
    } else {
      // Fallback to regular fetch if sendBeacon is not available
      await supabase.functions.invoke('manage-user-sessions', {
        body: { 
          action: 'end_session',
          session_id: currentSessionId
        }
      });
      logInfo('User session ended with fetch');
    }
    
    // Reset session state
    sessionStarted = false;
    currentSessionId = null;
  } catch (error) {
    logError('Error ending session', error);
  }
};

/**
 * Setup unload handler to track when users leave the site
 */
export const setupUnloadTracking = (): (() => void) => {
  const handleBeforeUnload = () => {
    if (sessionStarted && currentSessionId) {
      // Using sendBeacon for reliability during unload
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL || 'https://csozwfcytrcqzctrqooj.supabase.co'}/functions/v1/manage-user-sessions`;
      
      navigator.sendBeacon(
        functionUrl,
        JSON.stringify({ 
          action: 'end_session',
          session_id: currentSessionId
        })
      );
      logInfo('User session ended on page unload');
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
};

/**
 * Record a login attempt
 */
export const recordLoginAttempt = async (email: string, wasSuccessful: boolean): Promise<void> => {
  try {
    logInfo('Recording login attempt', { success: wasSuccessful });
    await supabase.functions.invoke('manage-user-sessions', {
      body: { 
        action: 'record_login_attempt',
        email,
        success: wasSuccessful
      }
    });
  } catch (error) {
    logError('Error recording login attempt', error);
  }
};
